import type {
  SegmentConditions,
  Channel,
  CostProfile,
  Device,
  DerivedType,
  EmailCadence,
  EnrichedEvent,
  EventType,
  ExecReportPayload,
  Funnel,
  FunnelSegmentBreakdown,
  FunnelStepMetrics,
  Goal,
  IngestStatusResponse,
  Insight,
  InsightStatus,
  OverviewResponse,
  Project,
  ProjectCost,
  ReplayChunkMeta,
  ReplayListResponse,
  SessionRecord,
  SustainabilityMonth,
} from "@dxp/schema";
import { normalizeInvite, normalizeMember } from "./lib/auth.js";
import type { InviteRow, MemberRow, Role } from "./lib/auth.js";

/**
 * §10 — Dashboard API istemcisi. Panelin sunucuyla TEK temas yüzeyi burasıdır;
 * sayfa bileşenleri `fetch` çağırmaz.
 *
 * Adres: her istek göreli `/api/...` yoluna gider — Vite dev sunucusu bunu
 * `VITE_API_BASE`'e proxy'ler (vite.config.ts). Böylece istemci tarafında CORS ve
 * gömülü port yoktur; ayrı origin'den servis edilen bir dağıtımda `VITE_API_PREFIX`
 * mutlak adrese çevrilerek override edilir.
 *
 * Auth (§27.3): panelin tarayıcı akışı COOKIE tabanlıdır — `dxp_sess` httpOnly
 * çerezi `credentials: 'include'` ile her isteğe gider; JS çerezi okuyamaz (XSS'te
 * token çalınamaz). Cookie'li TÜM mutasyonlarda `x-dxp-csrf: 1` başlığı zorunludur
 * (§27.3 — özel başlık cross-site sahtelenemez; başlıksız mutasyon 403).
 *
 * `ADMIN_TOKEN` bearer'ı KALDIRILMADI (§27.6 README değişmez kuralı): artık SERVİS
 * BELİRTECİdir (rol `owner`, tüm projeler, `users` kaydı yok, CSRF'ten muaf) ve
 * CI/e2e ile `tools/*` onu kullanmaya devam eder. Bu yüzden token AÇIKÇA
 * yapılandırılmışsa (localStorage veya `VITE_ADMIN_TOKEN`) bearer başlığı yine
 * gönderilir; sunucu bearer'ı cookie'den ÖNCE değerlendirir (§27.5 sıra).
 * Açık token yoksa bearer HİÇ gönderilmez — 'dev-admin-token' varsayılanının her
 * isteğe iliştirilmesi, cookie ile giriş yapmış kullanıcıyı geçersiz bir bearer'ın
 * arkasına saklardı.
 */

/* -------------------------------------------------------------------------- */
/* Yapılandırma                                                                */
/* -------------------------------------------------------------------------- */

/** `import.meta.env`'i `any` sızdırmadan okur. */
function viteEnv(key: string): string | undefined {
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  const value = env[key];
  return value === undefined || value === "" ? undefined : value;
}

/** §10 — Dashboard API prefix'i. Sondaki '/' temizlenir. */
export const API_PREFIX = (viteEnv("VITE_API_PREFIX") ?? "/api").replace(
  /\/+$/,
  "",
);

/** §13 — MVP tek projelidir; Layout'taki proje seçici bu değeri başlangıç kabul eder. */
export const ACTIVE_PID = "prj_demo";

/** Operatörün kendi token'ını yapıştırabildiği tarayıcı anahtarı. */
export const ADMIN_TOKEN_STORAGE_KEY = "dxp_admin_token";

/**
 * §27.6 — AÇIKÇA yapılandırılmış servis belirteci: localStorage → `VITE_ADMIN_TOKEN`.
 *
 * Varsayılana (`dev-admin-token`) DÜŞMEZ; `getAdminToken()`'dan farkı budur. İstek
 * katmanı bunu kullanır: token yoksa bearer başlığı hiç kurulmaz ve istek saf cookie
 * isteği olur (§27.3). e2e'ler token'ı `localStorage['dxp_admin_token']`'a yazdığı
 * için servis yolu aynen çalışmaya devam eder.
 *
 * Yerelde paneli oturumsuz (servis belirteciyle) sürdürmek isteyen operatör kök
 * `.env`'e `VITE_ADMIN_TOKEN=<ADMIN_TOKEN>` ekler ya da token'ı localStorage'a
 * yapıştırır; normal akış §27.8'deki gibi `bootstrap-admin` + `/giris` girişidir.
 */
export function getBearerToken(): string | undefined {
  try {
    const stored = globalThis.localStorage?.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (stored !== null && stored !== undefined && stored !== "") return stored;
  } catch {
    /* depolama erişilemez — env değerine düş */
  }
  return viteEnv("VITE_ADMIN_TOKEN");
}

/** §27.6 — bu sekme servis belirteciyle mi geziyor (rol `owner` kabul edilir)? */
export function hasServiceToken(): boolean {
  return getBearerToken() !== undefined;
}

/**
 * §10 uyumluluk yüzeyi: token YOKSA geliştirme varsayılanını döndürür.
 * İstek başlığı artık `getBearerToken()`'dan kurulur; bu fonksiyon, token'ı ekranda
 * göstermek/karşılaştırmak isteyen çağıranlar için korunur.
 */
export function getAdminToken(): string {
  return getBearerToken() ?? "dev-admin-token";
}

/*
 * §82.3 — `setAdminToken` SİLİNDİ (Faz 8/M42).
 *
 * "Layout ayar alanı" için yazılmıştı ama o alan hiç yapılmadı: fonksiyonun depoda
 * TEK referansı kendi tanımıydı. `ADMIN_TOKEN_STORAGE_KEY` ve OKUMA yolu
 * (`getBearerToken`) KALIR — e2e'ler token'ı localStorage'a yazarak servis belirteci
 * yolunu sürüyor.
 *
 * NEDEN UI yazmak yerine silmek: panele sır YAPIŞTIRTAN bir kutu, ekran paylaşımıyla
 * sızacak ikinci bir yüzey açardı ve operatörün zaten `VITE_ADMIN_TOKEN` yolu var.
 */

/* -------------------------------------------------------------------------- */
/* Hata tipi ve taşıma                                                          */
/* -------------------------------------------------------------------------- */

/** Ağ/HTTP hatalarının tek tipi — sayfalar `status` üzerinden 401/404 ayrımı yapar. */
export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  readonly body: string;
  /**
   * Sunucunun MAKİNE-OKUR hata kodu — gövdedeki `{"error":"weak_password"}` alanı;
   * yoksa `null`.
   *
   * NEDEN alan: durum kodu tek başına yetmiyor. §43.2'de tek bir 400 altında
   * `invalid_email` ve `weak_password`, tek bir 403 altında `email_unverified` ve
   * `csrf_required` yaşar; panel bunları ayırt edemezse ya yanlış cümleyi kurar ya da
   * yanlış düğmeyi ("doğrulama e-postasını yeniden gönder") gösterir. Ayrım gövde
   * METNİNDE arama yaparak da yapılabilirdi (`body.includes('weak_password')`) ama o
   * yöntem sunucunun Türkçe mesajı içinde geçen bir kelimeye de takılır — kod alanı
   * sözleşmenin kendisidir, mesaj ise kullanıcıya gösterilen serbest metin.
   */
  readonly code: string | null;

  constructor(status: number, url: string, message: string, body = "") {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.url = url;
    this.body = body;
    this.code = extractCode(body);
  }

  /** Token yanlış/eksik (§10 bearer). */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

function buildUrl(
  path: string,
  query?: Record<string, string | number | undefined>,
): string {
  const url = `${API_PREFIX}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs === "" ? url : `${url}?${qs}`;
}

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** §27.3 — CSRF başlığı gereken yöntemler (gövde değiştiren her şey). */
const MUTATION_METHODS: ReadonlySet<string> = new Set([
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
]);

/** §27.3 — "cookie'li TÜM mutasyonlarda özel başlık zorunlu"; değeri sabit '1'dir. */
export const CSRF_HEADER = "x-dxp-csrf";

interface RequestOptions {
  /** §26.6 aboneliği silmek için DELETE gerekir; sunucu CORS listesinde de vardır. */
  method?: HttpMethod;
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  signal?: AbortSignal;
  /**
   * §27.7 — 401'de global "girişe dön" kancasını ATLA.
   *
   * Yalnız oturum EDİNME uçları için: `/auth/login` yanlış parolada 401 döner, bu bir
   * oturum düşmesi değil form hatasıdır; kanca tetiklenirse kullanıcı yazdığı formu
   * kaybederek yeniden `/giris`'e atılırdı.
   */
  skipUnauthorizedHook?: boolean;
}

/* -------------------------------------------------------------------------- */
/* 401 kancası (§27.7)                                                          */
/* -------------------------------------------------------------------------- */

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * §27.7 — "Herhangi bir API yanıtı 401 gelirse `/giris`'e yönlendir".
 *
 * Yönlendirmenin kendisi router'a aittir; api katmanı sadece HABER VERİR. Kancayı
 * `App` içindeki köprü bileşeni kurar ve sökülürken `null`'lar.
 */
export function setUnauthorizedHandler(
  handler: UnauthorizedHandler | null,
): void {
  unauthorizedHandler = handler;
}

/** Kancayı tetikler; kanca yoksa (test/SSR) sessizdir. */
export function notifyUnauthorized(): void {
  if (unauthorizedHandler === null) return;
  try {
    unauthorizedHandler();
  } catch {
    /* yönlendirme hatası isteğin hata tipini bozmamalı */
  }
}

/**
 * Tek fetch sarmalayıcı: cookie + (varsa) bearer, CSRF başlığı, JSON gövde/yanıt,
 * hata normalizasyonu.
 * 204 ve boş gövde `undefined` döner (POST uçları `Promise<void>` sözleşmesi).
 */
async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    method = "GET",
    query,
    body,
    signal,
    skipUnauthorizedHook = false,
  } = options;
  const url = buildUrl(path, query);

  const headers: Record<string, string> = { Accept: "application/json" };

  // §27.6 — servis belirteci yalnız AÇIKÇA yapılandırılmışsa gider (bkz. getBearerToken).
  const bearer = getBearerToken();
  if (bearer !== undefined) headers["Authorization"] = `Bearer ${bearer}`;

  /*
   * §27.3 — CSRF başlığı mutasyonlara KOŞULSUZ eklenir; bearer isteklerinde sunucu
   * kontrolü atlar ama başlığın varlığı zararsızdır. Koşullu eklemek, token'ı olmayan
   * cookie oturumunda başlığın unutulmasına ve her mutasyonun 403 dönmesine yol açardı.
   */
  if (MUTATION_METHODS.has(method)) headers[CSRF_HEADER] = "1";

  if (body !== undefined) headers["Content-Type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
      // §27.3 — `dxp_sess` httpOnly çerezi gitsin; panel ayrı origin'den servis edilse de.
      credentials: "include",
    });
  } catch (cause) {
    // Ağ hatası / sunucu kapalı — 0 durumu ile tek tipe indir.
    const reason = cause instanceof Error ? cause.message : String(cause);
    throw new ApiError(0, url, `Sunucuya ulaşılamadı: ${reason}`);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    // §27.7 — oturum düştü: uygulama girişe dönsün (401 dışındaki hatalar sayfada kalır).
    if (response.status === 401 && !skipUnauthorizedHook) notifyUnauthorized();
    throw new ApiError(
      response.status,
      url,
      apiErrorMessage(response.status, text),
      text.slice(0, 500),
    );
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (text === "") return undefined as T;
  return JSON.parse(text) as T;
}

/** Kullanıcıya gösterilebilir Türkçe hata metni (§13 boş/hata durumları). */
function apiErrorMessage(status: number, body: string): string {
  const detail = extractMessage(body);
  /*
   * §27 — iki durum ARTIK AYRI: 401 "oturum yok/süresi doldu" (çözüm: yeniden giriş),
   * 403 "oturum var ama rol yetmiyor" (çözüm: proje sahibinden yetki istemek).
   * Aynı cümleyi vermek kullanıcıyı yanlış eyleme yönlendirirdi.
   */
  if (status === 401) {
    return (
      detail ?? "Oturum bulunamadı veya süresi doldu — yeniden giriş yapın."
    );
  }
  if (status === 403) {
    return detail ?? "Bu işlem için yetkiniz yok.";
  }
  if (status === 404) return detail ?? "Kayıt bulunamadı.";
  if (status >= 500)
    return detail ?? "Sunucu hatası — daha sonra tekrar deneyin.";
  return detail ?? `İstek başarısız (HTTP ${status}).`;
}

/**
 * Gövdedeki `error` alanını okur (`{"error":"weak_password","message":"…"}`).
 *
 * Yalnız STRING kodlar kabul edilir: bazı uçlar `error`'a nesne/dizi koyabilir ve
 * `String(obj)` "[object Object]" gibi bir "kod" üretip karşılaştırmaları sessizce
 * yanlış tarafa düşürürdü.
 */
function extractCode(body: string): string | null {
  if (body === "") return null;
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed !== null && typeof parsed === "object") {
      const code = (parsed as { error?: unknown }).error;
      if (typeof code === "string" && code !== "") return code;
    }
  } catch {
    /* JSON değil — kod yok sayılır */
  }
  return null;
}

function extractMessage(body: string): string | undefined {
  if (body === "") return undefined;
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed !== null && typeof parsed === "object") {
      const message =
        (parsed as { message?: unknown; error?: unknown }).message ??
        (parsed as { error?: unknown }).error;
      if (typeof message === "string" && message !== "") return message;
    }
  } catch {
    /* JSON değil — ham gövdeyi kullanma, jenerik metne düş */
  }
  return undefined;
}

/* -------------------------------------------------------------------------- */
/* Yanıt tipleri (şemada karşılığı olmayan panel-özgü sözleşmeler)              */
/* -------------------------------------------------------------------------- */

/** §13.5 oturum listesi satırı — `SessionRecord`'un panelde gösterilen alt kümesi. */
export type SessionListItem = Pick<
  SessionRecord,
  | 'session_id'
  | 'aid'
  | 'started_at'
  | 'ended_at'
  | 'duration_ms'
  | 'entry_url'
  | 'exit_url'
  | 'page_count'
  | 'event_count'
  | 'device'
  | 'browser'
  | 'os'
  | 'channel'
  | 'had_js_error'
  | 'had_api_error'
  | 'had_payment_error'
  | 'had_rage_click'
  | 'is_bounce'
  | 'converted'
  | 'revenue'
> & {
  /**
   * §11.2 — 1 ise oturum HENÜZ AÇIKTIR: satır `dxp.sessions`'tan değil, worker'ın
   * Redis penceresinden okunmuştur. `duration_ms`/`page_count` "şu ana kadar"dır ve
   * oturum kapanana (§11.5 — 30 dk hareketsizlik) kadar büyümeye devam eder.
   *
   * Eski sunucu sürümü bu alanı göndermez; `?? 0` ile kapanmış sayılır.
   */
  live?: number;
};

/** §13.5 zaman çizelgesi satırı — "tip ikonu, ts, url, önemli props" (replay v0). */
export type SessionEventItem = Pick<
  EnrichedEvent,
  "seq" | "ts" | "type" | "url" | "url_path" | "derived_type" | "props"
>;

/**
 * §22.7 chunk gövdesi satırı — oynatıcının tükettiği ham olay.
 *
 * Şemadaki `ReplayEvent`'ten kasıtlı olarak GEVŞEKTİR: orada `t` sekiz değerli bir
 * birleşimdir, burada `string`. Panel sunucudan gelen kaydı yalnızca OKUR ve eski
 * kayıtlar yeni panel sürümüyle oynatılmaya devam etmelidir; ileride şemaya eklenen
 * (ya da kaldırılan) bir olay tipi burada tip hatasına dönüşürse, tek bir tanımadığı
 * olay yüzünden tüm chunk'ın tipi bozulurdu. Oynatıcı bilinmeyen `t`'yi atlar.
 *
 * `ts` chunk'ın `started_at`'ine göre RÖLATİF ms'dir (§22.4/§22.6) — mutlak zaman
 * `chunk.started_at + event.ts`. Her chunk'ın ilk olayının `ts`'i 0'dır.
 */
export interface ReplayEventLite {
  t: string;
  ts: number;
  [key: string]: unknown;
}

/** §13.5 filtre kümesi (§10 `?filter=`). */
export const SESSION_FILTERS = [
  "error",
  "rage",
  "abandoned",
  "converted",
] as const;
export type SessionFilter = (typeof SESSION_FILTERS)[number];

/** §13.5 filtre etiketleri — sekme/çip metinleri. */
export const SESSION_FILTER_LABELS: Record<SessionFilter, string> = {
  error: "Hata",
  rage: "Rage click",
  abandoned: "Terk",
  converted: "Dönüşüm",
};

/** §13.3 segment seçici — cihaz veya kanal kırılımı. */
export interface FunnelSegmentFilter {
  dimension: "device" | "channel";
  value: Device | Channel | string;
}

/** §92.3 — kayıtlı segment satırı (sunucu `SegmentRecord`ının panel görünümü). */
export interface SegmentItem {
  id: string;
  pid: string;
  name: string;
  conditions: SegmentConditions;
  created_at: string;
}

/** §10 `GET /api/projects/:pid/funnel` yanıtı. */
export interface FunnelResponse {
  /** Adım kutuları + drop yüzdeleri (§11.6.3). */
  steps: FunnelStepMetrics[];
  /** Cihaz/kanal kırılımı — segment seçicinin doldurduğu liste. */
  segments: FunnelSegmentBreakdown[];
  /** Huninin kendisi (goal bağı, otomatik mi, ne zaman hesaplandı). */
  funnel?: Funnel | null;
  /**
   * §94 — `?compare=1` istendiğinde önceki 14 günlük pencerenin GENEL adım
   * metrikleri; `null` = önceki pencere boş (kıyas çizilmez).
   */
  previous_steps?: FunnelStepMetrics[] | null;
}

/* --- §23.4 ısı haritası ------------------------------------------------------ */

/** §23.4 `mode` — varsayılan `click`. */
export type HeatmapMode = "click" | "scroll";

/**
 * §23.4 `device` — API `tablet`i de kabul eder; panel toggle'ı yalnız
 * desktop/mobile gösterir (§23.5: tablet trafik payı düşük, harita seyrekleşir).
 */
export type HeatmapDeviceParam = "desktop" | "mobile" | "tablet";

/** §23.4 sorgu parametreleri — `path`/`device` zorunlu, gerisi sunucu varsayılanı. */
export interface HeatmapQuery {
  /** Parametrik desen (`/urun/:slug`) — sunucu LIKE'a çevirir. */
  path: string;
  device: HeatmapDeviceParam;
  mode?: HeatmapMode;
  /** 'YYYY-MM-DD'; verilmezse sunucu son 14 günü kullanır (azami aralık 90 gün). */
  from?: string;
  to?: string;
}

/**
 * §33.5 — liste uçlarının ORTAK zarfı.
 *
 * Çıplak dizi, "hepsi bu kadar" ile "ilk N tanesi"ni ayırt ettirmez; panel eksik listeyi
 * TAM sanardı. `truncated` bu ayrımı taşır ve tablolar altında "liste kırpıldı" uyarısına
 * dönüşür (§13 "boş/eksik durumu açıkça söyle").
 */
export interface ListResponse<T> {
  items: T[];
  limit: number;
  truncated: boolean;
}

/** §23.4 `GET /api/projects/:pid/heatmap` yanıtı. */
export interface HeatmapResponse {
  mode: HeatmapMode;
  /** mode=click; YALNIZ c>0 hücreler (maks 10000). */
  grid: { x: number; y: number; c: number }[];
  /** mode=scroll; bucket 0..19, c = views toplamı. */
  scroll: { bucket: number; c: number }[];
  totalClicks: number;
  /** `uniqExact(sid)` — MV'den değil, `events_enriched` üzerinden canlı (§23.4). */
  totalSessions: number;
  topSelectors: { selector: string; sessions: number; events: number }[];
  /**
   * §37.4 — ısı haritası arka planı (ekran görüntüsü). `null` ise panel canlı
   * iframe'e, o da engellenirse ızgaraya düşer.
   *
   * `url` İMZALI ve SÜRELİDİR (15 dk): bucket public değildir, bu yüzden bağlantı
   * paylaşılabilir/kalıcı bir adres DEĞİLDİR ve yenilemede yeniden üretilir.
   */
  background: { url: string; width: number; height: number } | null;
}

/** §23.4 yardımcı uç satırı — panelin path seçicisi ve iframe arka planı. */
export interface HeatmapPath {
  pattern: string;
  /** Desenin en yüksek `page_view` sayılı ham `url_path` örneği (iframe bunu yükler, §23.5). */
  samplePath: string;
  views: number;
}

/**
 * §23.4 `GET /api/projects/:pid/heatmap?path=&device=&from=&to=&mode=`.
 * Boş sonuç 200 + boş dizilerdir (404 DEĞİL) — boş durumu panel kendi çizer.
 */
export function getHeatmap(
  pid: string,
  params: HeatmapQuery,
  signal?: AbortSignal,
): Promise<HeatmapResponse> {
  return request<HeatmapResponse>(
    `/projects/${encodeURIComponent(pid)}/heatmap`,
    {
      query: {
        path: params.path,
        device: params.device,
        mode: params.mode,
        from: params.from,
        to: params.to,
      },
      signal,
    },
  );
}

/** §23.4 `GET /api/projects/:pid/heatmap/paths` — son 14 günün ilk 20 deseni. */
export function getHeatmapPaths(
  pid: string,
  signal?: AbortSignal,
): Promise<ListResponse<HeatmapPath>> {
  return request<ListResponse<HeatmapPath>>(
    `/projects/${encodeURIComponent(pid)}/heatmap/paths`,
    { signal },
  );
}

/* --- §24.7 SEO -------------------------------------------------------------- */

/**
 * §24.4 `SeoCheckResult` — sunucudan OKUNAN biçim.
 *
 * `id` daraltılmış birleşim DEĞİL `string`'dir: kontrol kataloğu crawler'ın
 * sözleşmesidir ve büyüyebilir (§24.4). Tek tanımadığı kontrol yüzünden tüm satırın
 * tipi bozulmamalı — `ReplayEventLite` ile aynı gerekçe. Katalog bilgisi (etiket, ceza)
 * `lib/seo-checks.ts`'te tutulur; bilinmeyen kimlik ham hâliyle gösterilir.
 */
export interface SeoCheckResult {
  id: string;
  /** §24.4 ceza puanı (IMG_ALT'ta oransal, BROKEN_INTERNAL'da 0). */
  penalty: number;
  /** örn. { len: 72 } | { ratio: 0.4, missing: 6, total: 15 } | { referrers: [...] } */
  detail?: Record<string, unknown>;
}

/** §24.5 `seo_scans` satırı — son TAMAMLANMIŞ tarama. */
export interface SeoScan {
  id: number;
  pid: string;
  /** ISO-8601 */
  started_at: string;
  /** ISO-8601; bu uçta her zaman doludur (yarım tarama dönmez). */
  finished_at: string | null;
  pages: number;
  /** 0..100 site skoru (§24.4); yoksa null — panel '—' gösterir, 0 UYDURMAZ. */
  score: number | null;
  /** check → sayfa sayısı; site-geneli checkler 1. */
  summary: Record<string, number>;
}

/** §24.5 `seo_pages` satırı — detay çekmecesinin kaynağı. */
export interface SeoPage {
  id: number;
  url: string;
  /** HTTP durum kodu; 0 = timeout (§24.3). */
  status: number;
  /** Ölçülemediyse null. */
  ttfb_ms: number | null;
  checks: SeoCheckResult[];
  score: number;
}

/** §24.7 `GET /api/projects/:pid/seo` yanıtı. */
export interface SeoResponse {
  /** Hiç tarama yoksa null (uç 404 DEĞİL 200 döner — boş durumu panel çizer). */
  scan: SeoScan | null;
  /** check → en çok 3 örnek URL. Site-geneli kontrollerde anahtar YOKTUR. */
  samples: Record<string, string[]>;
  /** `finished_at IS NULL` tarama var mı — "Yeniden tara" düğmesi buna bakar. */
  running: boolean;
}

/** §24.7 `/seo/pages` sorgu parametreleri; limit varsayılanı 50, üst sınırı 200. */
export interface SeoPagesQuery {
  check?: string;
  limit?: number;
}

/** §24.7 — son tamamlanmış tarama + check başına örnek URL'ler. */
export function getSeo(
  pid: string,
  signal?: AbortSignal,
): Promise<SeoResponse> {
  return request<SeoResponse>(`/projects/${encodeURIComponent(pid)}/seo`, {
    signal,
  });
}

/** §24.7 — o check'i taşıyan sayfa satırları (çekmece içeriği). */
export function getSeoPages(
  pid: string,
  params: SeoPagesQuery = {},
  signal?: AbortSignal,
): Promise<ListResponse<SeoPage>> {
  return request<ListResponse<SeoPage>>(
    `/projects/${encodeURIComponent(pid)}/seo/pages`,
    {
      query: { check: params.check, limit: params.limit },
      signal,
    },
  );
}

/**
 * §24.7 — taramayı kuyruğa alır (202). Sürmekte olan tarama varsa sunucu 409 döner ve
 * `ApiError.status === 409` ile fırlatılır; çağıran "tarama sürüyor" ayrımını MESAJ
 * metnine göre değil DURUM KODUNA göre yapar.
 */
export function startSeoScan(pid: string): Promise<{ queued: boolean }> {
  return request<{ queued: boolean }>(
    `/projects/${encodeURIComponent(pid)}/seo/scan`,
    {
      method: "POST",
    },
  );
}

/** Sağlık ucu — bağımlılık başına tek satır (panel üst çubuğu göstergesi). */
export interface HealthDependency {
  name: string;
  ok: boolean;
  detail?: string;
}

export interface HealthResponse {
  status: "ok" | "degraded" | "down";
  uptime_s: number;
  version?: string;
  deps: HealthDependency[];
}

/** §19.6 iç panel — müşteri başına COGS ve brüt marj. */
export interface CostsResponse {
  pid: string;
  costProfile: CostProfile;
  /** Son 30 günün toplam maliyeti (TL). */
  monthlyCostTl: number;
  /** `projects.plan_revenue_tl_month` (§19.6). */
  planRevenueTl: number;
  /** Brüt marj — YÜZDE PUANI (0..100). */
  grossMarginPct: number;
  /** §19.6 — marj < %70 ise uyarı ("eco profiline alınmalı / üst pakete geçmeli"). */
  marginWarning: boolean;
  monthlyEvents: number;
  /** Günlük kırılım — `project_costs` satırları, eskiden yeniye. */
  daily: ProjectCost[];
  /**
   * §19.5 sürdürülebilirlik metriği — CSRD raporuna girdi olabilecek aylık özet.
   * OPSİYONEL: 005_sustainability.sql öncesi sürüme bakan bir panel de bu tipi
   * kullanabilsin diye; alan yoksa kart gizlenir, sıfır uydurulmaz.
   * energyKwh/co2eG SON 30 GÜNÜN toplamıdır (`daily` ile aynı pencere);
   * `months` takvim ayı kırılımıdır (son 12 ay) — ikisi eşit OLMAYABİLİR.
   * avgGridIntensity: enerjiyle ağırlıklı gCO2e/kWh; 0 = bilinmiyor.
   */
  sustainability?: {
    energyKwh: number;
    co2eG: number;
    avgGridIntensity: number;
    months: SustainabilityMonth[];
  };
}

/* -------------------------------------------------------------------------- */
/* Uçlar (§10)                                                                  */
/* -------------------------------------------------------------------------- */

/** §10 `GET /api/projects/:pid/overview?from&to` — from/to 'YYYY-MM-DD'. */
export function getOverview(
  pid: string,
  from: string,
  to: string,
  signal?: AbortSignal,
  /** §94 — önceki eşit dönemin KPI'ları da istensin mi (`previous` alanı). */
  compare = false,
): Promise<OverviewResponse> {
  return request<OverviewResponse>(
    `/projects/${encodeURIComponent(pid)}/overview`,
    {
      query: { from, to, compare: compare ? "1" : undefined },
      signal,
    },
  );
}

/**
 * §13 `GET /api/projects/:pid/ingest-status` — "SDK gerçekten kurulu mu?".
 *
 * Panelin geri kalanı `daily_agg`'den okur ve oraya satır ancak OTURUM KAPANINCA
 * girer (§11.5 — 30 dk hareketsizlik). Bu uç `events_enriched`'e bakar: etiket
 * çalışıyorsa cevap saniyeler içinde gelir. Boş durum metnini ("SDK'yı ekleyin")
 * gerçekten SDK'sı olmayan kullanıcıya saklamak için gereklidir.
 *
 * ESKİ SUNUCU: uç yoksa 404 gelir; ÇAĞIRAN bunu "hata" değil "bilinmiyor" saymalıdır
 * (üye listesi ucundaki 404 ile aynı desen).
 */
export function getIngestStatus(
  pid: string,
  signal?: AbortSignal,
): Promise<IngestStatusResponse> {
  return request<IngestStatusResponse>(
    `/projects/${encodeURIComponent(pid)}/ingest-status`,
    {
      signal,
    },
  );
}

/** §10 `GET /api/projects/:pid/goals` — keşfedilen hedefler + confidence (§11.5). */
/* ---------------------------- Faz 6 §62/§65 deneyler ---------------------------- */

export interface ExperimentVariantDef {
  key: string;
  weight: number;
  /** §107.3 KARAR 2 — bu varyant NE gösteriyor ("Sepete Ekle" / "Hemen Al"). */
  label?: string;
  /**
   * §109 — SPLIT-URL: bu varyantın sayfası (YOL, mutlak adres değil). Deney
   * split-URL'dir ancak ve ancak TÜM varyantları bunu taşıyorsa (KARAR 5).
   */
  url?: string;
}

export interface Experiment {
  id: string;
  pid: string;
  name: string;
  hypothesis: string;
  variants: ExperimentVariantDef[];
  goalId: number | null;
  status: "draft" | "running" | "stopped" | "concluded";
  winner: string | null;
  startedAt: string | null;
  concludedAt: string | null;
  /**
   * §102.3 — motorun UYGULAYACAĞI otomatik kapanış tarihi (`experiment-job`
   * damgalar). Panel bunu HESAPLAMAZ: `EXP_MAX_DAYS` worker'ın ortamındadır ve
   * kurulumdan kuruluma değişir. `null` = deney henüz değerlendirilmedi → tarih
   * basılmaz (§101.2 KARAR 1).
   */
  autoConcludeAt: string | null;
  /** §107.3 KARAR 1 — deneyin koştuğu URL deseni; `null` = yüzey tanımsız. */
  targetPattern: string | null;
  /**
   * §107.3 KARAR 3 — atanmış oturumlardan kaçı hedef sayfayı gördü (motor yazar).
   * `null` = henüz sayılmadı; `0` = hiçbiri görmedi → deney muhtemelen sitede
   * UYGULANMAMIŞ. İkisi panelde AYRI hâllerdir.
   */
  targetSeenSessions: number | null;
  createdAt: string;
}

export interface ExperimentResultRow {
  experimentId: string;
  variant: string;
  sessions: number;
  conversions: number;
  pValue: number | null;
  liftLow: number | null;
  liftMid: number | null;
  liftHigh: number | null;
  computedAt: string;
}

export function getExperiments(
  pid: string,
  signal?: AbortSignal,
): Promise<Experiment[]> {
  return request<Experiment[]>(
    `/projects/${encodeURIComponent(pid)}/experiments`,
    { signal },
  );
}

export function createExperiment(
  pid: string,
  body: {
    name: string;
    hypothesis?: string;
    variants: ExperimentVariantDef[];
    goalId?: number | null;
    /** §107.5 — test yüzeyi; '/' ile başlar, boş bırakılabilir. */
    targetPattern?: string | null;
  },
): Promise<Experiment> {
  return request<Experiment>(
    `/projects/${encodeURIComponent(pid)}/experiments`,
    {
      method: "POST",
      body,
    },
  );
}

export function setExperimentStatus(
  id: string,
  status: "running" | "stopped",
): Promise<Experiment> {
  return request<Experiment>(`/experiments/${encodeURIComponent(id)}/status`, {
    method: "POST",
    body: { status },
  });
}

export function getExperimentResults(
  id: string,
  signal?: AbortSignal,
): Promise<{ experiment: Experiment; results: ExperimentResultRow[] }> {
  return request(`/experiments/${encodeURIComponent(id)}/results`, { signal });
}

export function getGoals(pid: string, signal?: AbortSignal): Promise<Goal[]> {
  return request<Goal[]>(`/projects/${encodeURIComponent(pid)}/goals`, {
    signal,
  });
}

/** §10 `POST /api/goals/:id/confirm` (§13.4 Onayla). */
export function confirmGoal(id: number): Promise<void> {
  return request<void>(`/goals/${encodeURIComponent(String(id))}/confirm`, {
    method: "POST",
  });
}

/** §10 `POST /api/goals/:id/reject` (§13.4 Reddet). */
export function rejectGoal(id: number): Promise<void> {
  return request<void>(`/goals/${encodeURIComponent(String(id))}/reject`, {
    method: "POST",
  });
}

/** §10 `GET /api/projects/:pid/funnel` — segment verilirse o kırılım öne çıkar (§13.3). */
export function getFunnel(
  pid: string,
  segment?: FunnelSegmentFilter,
  signal?: AbortSignal,
  /** §92.3 — kayıtlı segment id'si; `segment` ile birlikte VERİLMEZ (sunucu 400 der). */
  segmentId?: string,
  /** §94 — önceki 14 günlük pencerenin adım metrikleri de istensin mi. */
  compare = false,
): Promise<FunnelResponse> {
  const query: Record<string, string | undefined> =
    segmentId !== undefined
      ? { segment: segmentId }
      : segment
        ? { dimension: segment.dimension, value: segment.value }
        : {};
  if (compare) query["compare"] = "1";
  return request<FunnelResponse>(
    `/projects/${encodeURIComponent(pid)}/funnel`,
    {
      query,
      signal,
    },
  );
}

/** §10 `GET /api/projects/:pid/insights?status=` — sunucu priority'ye göre sıralar (§12.2). */
export function getInsights(
  pid: string,
  status?: InsightStatus,
  signal?: AbortSignal,
): Promise<ListResponse<Insight>> {
  return request<ListResponse<Insight>>(
    `/projects/${encodeURIComponent(pid)}/insights`,
    {
      query: { status },
      signal,
    },
  );
}

/** §10 `POST /api/insights/:id/status` — §13.2 Gördüm / Uyguladım / Yoksay. */
export function setInsightStatus(
  id: number,
  status: InsightStatus,
): Promise<void> {
  return request<void>(`/insights/${encodeURIComponent(String(id))}/status`, {
    method: "POST",
    body: { status },
  });
}

/** §10 `GET /api/projects/:pid/sessions?filter=` (§13.5). */
export function getSessions(
  pid: string,
  filter?: SessionFilter,
  signal?: AbortSignal,
  /** §92.3 — kayıtlı segment; davranış filtresiyle AND bileşir (ayrı eksenler). */
  segmentId?: string,
  /**
   * §13.3 — funnel ADIM indeksi; yalnız `filter='abandoned'` ile anlamlıdır.
   * "k adımında terk etti" = k'yı gördü, k+1'i görmedi.
   */
  step?: number,
): Promise<ListResponse<SessionListItem>> {
  return request<ListResponse<SessionListItem>>(
    `/projects/${encodeURIComponent(pid)}/sessions`,
    {
      query: { filter, segment: segmentId, step: step === undefined ? undefined : String(step) },
      signal,
    },
  );
}

/* ------------------------------- §92 segmentler ------------------------------ */

/** §92.3 `GET /api/projects/:pid/segments` — ad sırasıyla. */
export function getSegments(
  pid: string,
  signal?: AbortSignal,
): Promise<{ items: SegmentItem[] }> {
  return request<{ items: SegmentItem[] }>(
    `/projects/${encodeURIComponent(pid)}/segments`,
    {
      signal,
    },
  );
}

/** §92.3 `POST` — 400 invalid_segment/invalid_name · 409 duplicate_name/segment_cap. */
export function createSegment(
  pid: string,
  name: string,
  conditions: SegmentConditions,
): Promise<SegmentItem> {
  return request<SegmentItem>(`/projects/${encodeURIComponent(pid)}/segments`, {
    method: "POST",
    body: { name, conditions },
  });
}

/** §93.3 `GET /api/projects/:pid/retention` yanıtı. */
export interface RetentionWeekRow {
  cohort: string;
  /**
   * §103.3 — kohort haftasının Pazartesi'si (`YYYY-MM-DD`). Panel insan tarihini
   * bundan biçimler; ISO etiketini tarihe geri çevirmeye ÇALIŞMAZ.
   */
  start: string;
  size: number;
  /** +0..+8 yüzdeleri; olgunlaşmamış hafta null (§93.1). */
  cells: (number | null)[];
  /** Aynı hücrelerin ham sayıları — `title` kesri (§93.4). */
  counts: (number | null)[];
}
export interface RetentionResponse {
  weeks: RetentionWeekRow[];
  computed_at: string;
}

/** §93.3 — kohort matrisi; veri yoksa `weeks: []` (404 değil). */
export function getRetention(
  pid: string,
  signal?: AbortSignal,
): Promise<RetentionResponse> {
  return request<RetentionResponse>(
    `/projects/${encodeURIComponent(pid)}/retention`,
    { signal },
  );
}

/** §92.3 `DELETE` — idempotent 204. */
export function deleteSegment(pid: string, id: string): Promise<void> {
  return request<void>(
    `/projects/${encodeURIComponent(pid)}/segments/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

/** §10 `GET /api/projects/:pid/sessions/:sid/events` — replay v0 zaman çizelgesi (§13.5). */
export function getSessionEvents(
  pid: string,
  sid: string,
  signal?: AbortSignal,
): Promise<ListResponse<SessionEventItem>> {
  return request<ListResponse<SessionEventItem>>(
    `/projects/${encodeURIComponent(pid)}/sessions/${encodeURIComponent(sid)}/events`,
    { signal },
  );
}

/**
 * §22.7 `GET /api/projects/:pid/sessions/:sid/replay` — GÖVDESİZ chunk listesi.
 *
 * Oturumlar sayfası kaydın varlığını (`chunks.length > 0`) bu uçtan öğrenir; gövdeler
 * megabaytlarca olabildiği için liste ayrı tutulur ve gövde chunk chunk çekilir.
 */
export function getSessionReplay(
  pid: string,
  sid: string,
  signal?: AbortSignal,
): Promise<ReplayListResponse> {
  return request<ReplayListResponse>(
    `/projects/${encodeURIComponent(pid)}/sessions/${encodeURIComponent(sid)}/replay`,
    { signal },
  );
}

/**
 * §22.7 `GET /api/projects/:pid/sessions/:sid/replay/:seq` — tek chunk'ın olay dizisi.
 *
 * Oynatıcı chunk'ları sırayla ve isteğe bağlı (lazy) çeker: tüm kaydı peşin indirmek
 * uzun oturumlarda ilk kareyi geciktirirdi. Dönen olayların `ts`'i bu chunk'ın
 * `started_at`'ine rölatiftir (§22.6) — çağıran mutlak zamanı kendisi toplar.
 */
export function getReplayChunk(
  pid: string,
  sid: string,
  seq: number,
  signal?: AbortSignal,
): Promise<ReplayEventLite[]> {
  return request<ReplayEventLite[]>(
    `/projects/${encodeURIComponent(pid)}/sessions/${encodeURIComponent(sid)}/replay/${encodeURIComponent(String(seq))}`,
    { signal },
  );
}

/* --- §26.6 e-posta abonelikleri --------------------------------------------- */

/**
 * §26.4 `email_subscriptions` satırının PANELE açılan yüzü.
 *
 * `token` alanı YOKTUR ve olmamalıdır: confirm/unsubscribe linklerinin tek sırrı odur
 * (§26.3), sunucu da listede döndürmez. Adres operasyonel veridir (§26.1) — yalnız
 * Postgres'te durur, olay şemasına/ClickHouse'a asla yazılmaz.
 */
export interface EmailSubscription {
  id: number;
  email: string;
  /** §26.3 — 'weekly' (varsayılan) | 'daily'. */
  cadence: EmailCadence;
  /** §26.3 double opt-in — rapor YALNIZ true olana gider ('onay bekliyor' rozeti false'ta). */
  confirmed: boolean;
  /** ISO-8601 */
  created_at: string;
}

/** §26.6 — abone listesi (proje başına, tavan `EMAIL_MAX_SUBS_PER_PID`). */
export function listSubscriptions(
  pid: string,
  signal?: AbortSignal,
): Promise<EmailSubscription[]> {
  return request<EmailSubscription[]>(
    `/projects/${encodeURIComponent(pid)}/subscriptions`,
    {
      signal,
    },
  );
}

/**
 * §26.6 — abone ekler (201) ve onay e-postasını kuyruğa alır. Hatalar DURUM KODUNDAN
 * ayrılır: 400 geçersiz adres/cadence, 409 tavan aşımı, 503 migration eksik.
 */
export function addSubscription(
  pid: string,
  input: { email: string; cadence?: EmailCadence },
): Promise<EmailSubscription> {
  return request<EmailSubscription>(
    `/projects/${encodeURIComponent(pid)}/subscriptions`,
    {
      method: "POST",
      body: {
        email: input.email,
        ...(input.cadence === undefined ? {} : { cadence: input.cadence }),
      },
    },
  );
}

/** §26.6 — aboneyi siler (204). Bilinmeyen id 404 döner. */
export function deleteSubscription(pid: string, id: number): Promise<void> {
  return request<void>(
    `/projects/${encodeURIComponent(pid)}/subscriptions/${encodeURIComponent(String(id))}`,
    { method: "DELETE" },
  );
}

/* --- §72 proje yaşam döngüsü ------------------------------------------------- */

/**
 * §72.2 — proje ayarları. Verilmeyen alan sunucuda DEĞİŞMEZ.
 *
 * `domain: ''` "boşalt" demektir (heatmap iframe'i kapanır), alanı hiç göndermemek
 * "dokunma" — ikisi AYRI şeydir ve sözleşme bunu korur.
 */
export function updateProjectSettings(
  pid: string,
  body: { name?: string; domain?: string; cost_profile?: CostProfile },
  signal?: AbortSignal,
): Promise<{ project: OrgProject }> {
  return request<{ project: OrgProject }>(
    `/projects/${encodeURIComponent(pid)}/settings`,
    {
      method: "POST",
      body,
      signal,
    },
  );
}

/**
 * §72.3 — API anahtarını döndürür. Yanıt `api_key` TAŞIR ve YALNIZ burada taşır;
 * eski anahtar bu çağrıyla ANINDA geçersizleşir (§71.2 KARAR 2).
 */
export function rotateProjectKey(
  pid: string,
  signal?: AbortSignal,
): Promise<{ project: Project }> {
  return request<{ project: Project }>(
    `/projects/${encodeURIComponent(pid)}/key/rotate`,
    {
      method: "POST",
      signal,
    },
  );
}

/** §72.4 — arşivle / geri al. SİLME fiili YOKTUR: veri durur, `/collect` kapanır. */
export function setProjectArchived(
  pid: string,
  archived: boolean,
  signal?: AbortSignal,
): Promise<{ project: OrgProject }> {
  return request<{ project: OrgProject }>(
    `/projects/${encodeURIComponent(pid)}/${archived ? "archive" : "unarchive"}`,
    { method: "POST", signal },
  );
}

/* --- §75 panel bildirimleri --------------------------------------------------- */

/**
 * §75.1 — bildirim satırı. `kind` AYRI bir katalog DEĞİL, §47.1 webhook olay
 * adlarının kendisidir (§71.2 KARAR 3): aynı olay üç kanaldan (e-posta · webhook ·
 * zil) tek gerçek kaynaktan duyurulur.
 */
export interface OrgNotice {
  id: number;
  org_id: string;
  kind: WebhookEventName;
  payload: Record<string, unknown>;
  created_at: string;
  read_at: string | null;
}

export interface OrgNoticesResponse {
  notices: OrgNotice[];
  /** Okunmamış SAYISI — zil rozeti bunu gösterir (liste tavanından bağımsız). */
  unread: number;
  /** §33.5 — liste tavana takıldı mı. */
  truncated: boolean;
}

export function getOrgNotices(
  oid: string,
  signal?: AbortSignal,
): Promise<OrgNoticesResponse> {
  return request<OrgNoticesResponse>(
    `/orgs/${encodeURIComponent(oid)}/notices`,
    { signal },
  );
}

/** §75.3 — `upTo` DAHİL hepsini okundu yapar (tek tek okundu makinesi YOKTUR). */
export function markOrgNoticesRead(
  oid: string,
  upTo: number,
  signal?: AbortSignal,
): Promise<{ ok: true; read: number }> {
  return request<{ ok: true; read: number }>(
    `/orgs/${encodeURIComponent(oid)}/notices/read`,
    {
      method: "POST",
      body: { upTo },
      signal,
    },
  );
}

/* --- §42.5 organizasyonlar --------------------------------------------------- */

/** §42.3 org rolleri — PROJE rolleriyle (viewer/editor/owner) KARIŞTIRILMAZ. */
export type OrgRole = "member" | "admin" | "owner";

export interface Org {
  id: string;
  name: string;
  slug: string;
  created_at: string;
}

/** §42.5 — org seçicisinin satırı: org bilgisi + kullanıcının O ORG'DAKİ rolü. */
export interface OrgSummary extends Org {
  role: OrgRole;
}

/** §42.5 org proje listesi satırı — `api_key` SUNUCUDA ayıklanır (SDK sırrıdır). */
export type OrgProject = Omit<Project, "api_key">;

/**
 * §42.5 `GET /api/orgs` — kullanıcının org'ları.
 *
 * Org'suz kullanıcı BOŞ liste alır (403 DEĞİL): §43.2 sihirbazının giriş noktası budur.
 */
export function getOrgs(signal?: AbortSignal): Promise<{ orgs: OrgSummary[] }> {
  return request<{ orgs: OrgSummary[] }>("/orgs", { signal });
}

export function createOrg(
  body: { name: string; slug?: string },
  signal?: AbortSignal,
): Promise<{ org: OrgSummary }> {
  return request<{ org: OrgSummary }>("/orgs", {
    method: "POST",
    body,
    signal,
  });
}

/**
 * §42.5 — org adını değiştirir. `slug` DEĞİŞTİRİLEMEZ (SSO eşleşmesi ve paylaşılmış
 * bağlantılar ona bağlıdır) — uç zaten yalnız `name` okur.
 *
 * §72.5/6: bu uç sunucuda M23'ten beri VARDI ama istemcisi hiç yazılmamıştı; org
 * adını değiştirmenin panelde hiçbir yolu yoktu.
 */
export function updateOrgSettings(
  oid: string,
  body: { name: string },
  signal?: AbortSignal,
): Promise<{ org: Org }> {
  return request<{ org: Org }>(`/orgs/${encodeURIComponent(oid)}/settings`, {
    method: "POST",
    body,
    signal,
  });
}

/** §42.5 — org'un projeleri; panelin proje seçicisi BUNU kullanır (org'a filtreli). */
export function getOrgProjects(
  oid: string,
  signal?: AbortSignal,
): Promise<{ projects: OrgProject[] }> {
  return request<{ projects: OrgProject[] }>(
    `/orgs/${encodeURIComponent(oid)}/projects`,
    {
      signal,
    },
  );
}

/**
 * §42.5 — org içinde proje açar.
 *
 * Yanıt `api_key` TAŞIR ve YALNIZ burada taşır: kullanıcı onu SDK snippet'ine
 * yapıştıracaktır. Liste ucunda görünmez.
 */
export function createOrgProject(
  oid: string,
  body: { name: string; domain?: string; pid?: string },
  signal?: AbortSignal,
): Promise<{ project: Project }> {
  return request<{ project: Project }>(
    `/orgs/${encodeURIComponent(oid)}/projects`,
    {
      method: "POST",
      body,
      signal,
    },
  );
}

export interface OrgMember {
  user_id: string;
  email: string;
  full_name: string | null;
  role: OrgRole;
  created_at: string;
}

export function getOrgMembers(
  oid: string,
  signal?: AbortSignal,
): Promise<{ members: OrgMember[] }> {
  return request<{ members: OrgMember[] }>(
    `/orgs/${encodeURIComponent(oid)}/members`,
    { signal },
  );
}

export function setOrgMemberRole(
  oid: string,
  userId: string,
  role: OrgRole,
  signal?: AbortSignal,
): Promise<{ ok: true; role: OrgRole }> {
  return request<{ ok: true; role: OrgRole }>(
    `/orgs/${encodeURIComponent(oid)}/members/${encodeURIComponent(userId)}/role`,
    { method: "POST", body: { role }, signal },
  );
}

/**
 * §42.5 — org üyeliğini KALDIRIR (org:admin).
 *
 * Proje üyeliğini SİLMEZ: iki uzay ayrıdır (§42.2) ve org'dan çıkarılan bir kullanıcı
 * projelerine erişmeye devam edebilir. Panelin bunu SÖYLEMESİ gerekir — sessiz bir
 * "kaldır", operatöre yaptığından fazlasını yaptığını düşündürürdü.
 */
export function removeOrgMember(
  oid: string,
  userId: string,
  signal?: AbortSignal,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/orgs/${encodeURIComponent(oid)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE", signal },
  );
}

/* --- §46 SSO (OIDC) ---------------------------------------------------------- */

/**
 * §46.5 org SSO yapılandırması — `clientSecret` BU TİPTE YOKTUR.
 *
 * Sunucu `publicSso` ile yalnız `clientSecretSet` boolean'ını döner (§46.5): sır bir
 * kez yazılır, bir daha OKUNMAZ. Panelin onu maskeleyerek göstermesi bile gereksiz
 * bir risk olurdu — "yazılı mı?" sorusunun cevabı yeterlidir.
 */
export interface OrgSso {
  orgId: string;
  issuer: string;
  clientId: string;
  /** Sırrın YAZILI olup olmadığı; değerin kendisi asla dönmez. */
  clientSecretSet: boolean;
  emailDomain: string | null;
  enforced: boolean;
  updatedAt: string;
}

/**
 * §46.5 — yapılandırma YOKSA 404 DEĞİL `{sso:null}` döner: "henüz kurulmamış" normal
 * bir durumdur ve ayarlar ekranını hata hâline sokmamalıdır.
 */
export function getOrgSso(
  oid: string,
  signal?: AbortSignal,
): Promise<{ sso: OrgSso | null; planAllows: boolean }> {
  return request<{ sso: OrgSso | null; planAllows: boolean }>(
    `/orgs/${encodeURIComponent(oid)}/sso`,
    { signal },
  );
}

export function saveOrgSso(
  oid: string,
  body: {
    issuer: string;
    clientId: string;
    /** BOŞ gönderilirse sunucu MEVCUT sırrı korur (§46.5) — form her kayıtta sır istemez. */
    clientSecret?: string;
    emailDomain?: string;
    enforced?: boolean;
  },
  signal?: AbortSignal,
): Promise<{ sso: OrgSso }> {
  return request<{ sso: OrgSso }>(`/orgs/${encodeURIComponent(oid)}/sso`, {
    method: "POST",
    body,
    signal,
  });
}

/**
 * §46.3/1 + §46.8 — kurumsal giriş başlangıç adresi.
 *
 * ADRESİN KENDİSİ GÖNDERİLMEZ, yalnız ALAN ADI: e-posta operasyonel PII'dır (§26.1)
 * ve GET sorgusunda erişim loglarına düşerdi — "panel adresi kendi tarafında böler"
 * kararı budur (§46.8). Bu bir `fetch` DEĞİL bir SEYİRDİR: uç IdP'ye 302 döner ve
 * tarayıcının oraya GİTMESİ gerekir; `fetch` yönlendirmeyi izleyip CORS'a takılırdı.
 */
export function oidcStartUrl(email: string): string | null {
  const domain = email.trim().toLowerCase().split("@")[1] ?? "";
  if (domain === "" || !domain.includes(".")) return null;
  return `${API_PREFIX}/auth/oidc/start?domain=${encodeURIComponent(domain)}`;
}

/* --- §47 webhook'lar --------------------------------------------------------- */

/**
 * §47.2 uç kaydı — `secret` BU TİPTE YOKTUR.
 *
 * Sunucu tarafındaki `WebhookRecord` ile birebir aynı karar (`@dxp/store`): sır
 * yalnız YARATMA yanıtında, gövdenin kökünde döner. Tipte olmaması, bir bileşenin
 * onu ekrana kalıcı olarak basmasını derleme zamanında imkânsız kılar.
 */
export interface Webhook {
  id: number;
  orgId: string;
  url: string;
  events: string[];
  active: boolean;
  createdAt: string;
}

export interface WebhookDelivery {
  id: number;
  webhookId: number;
  event: string;
  payload: Record<string, unknown>;
  status: "pending" | "delivered" | "dead";
  attempts: number;
  /** Son HTTP kodu; `0` = ağ hatası (§47.2). Hiç denenmediyse null. */
  lastStatus: number | null;
  nextRetryAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

/**
 * §47.1 olay kataloğu — panelin abone olma kutuları.
 *
 * Liste burada SABİTTİR çünkü sunucuda da sabittir (`WEBHOOK_EVENTS`, kapalı küme) ve
 * onu okuyacak bir uç YOKTUR. Ayrışırsa panel geçersiz bir ad gönderir ve uç 400
 * `invalid_events` döner — sessiz bir bozulma değil, görünür bir hata.
 */
export const WEBHOOK_EVENT_CATALOG = [
  "insight.created",
  "seo.scan_finished",
  "email.report_sent",
  "quota.threshold",
  "subscription.updated",
] as const;

/**
 * §47.1 olay adı. §75 panel bildirimleri de BU tipi kullanır — zil ve webhook aynı
 * kataloğa bakar (§71.2 KARAR 3); ayrı bir "bildirim türü" tipi, iki kanalın zamanla
 * ayrışmasına izin verirdi.
 */
export type WebhookEventName = (typeof WEBHOOK_EVENT_CATALOG)[number];

export function getWebhooks(
  oid: string,
  signal?: AbortSignal,
): Promise<{ webhooks: Webhook[] }> {
  return request<{ webhooks: Webhook[] }>(
    `/orgs/${encodeURIComponent(oid)}/webhooks`,
    { signal },
  );
}

/** §47.2 — yanıt sırrı BİR KEZ taşır; çağıran onu saklamaz, kullanıcıya gösterir. */
export function createWebhook(
  oid: string,
  body: { url: string; events: string[] },
  signal?: AbortSignal,
): Promise<{ webhook: Webhook; secret: string }> {
  return request<{ webhook: Webhook; secret: string }>(
    `/orgs/${encodeURIComponent(oid)}/webhooks`,
    { method: "POST", body, signal },
  );
}

export function deleteWebhook(
  oid: string,
  id: number,
  signal?: AbortSignal,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/orgs/${encodeURIComponent(oid)}/webhooks/${id}`,
    {
      method: "DELETE",
      signal,
    },
  );
}

/** §47.5/2 — teslimat geçmişi; `dead` sayısı ve `truncated` sunucudan gelir (§33.5). */
export function getWebhookDeliveries(
  oid: string,
  id: number,
  signal?: AbortSignal,
): Promise<{
  deliveries: WebhookDelivery[];
  dead: number;
  truncated: boolean;
}> {
  return request<{
    deliveries: WebhookDelivery[];
    dead: number;
    truncated: boolean;
  }>(`/orgs/${encodeURIComponent(oid)}/webhooks/${id}/deliveries`, { signal });
}

/** §47.3 — `dead` satırı canlandırır. YENİ SATIR AÇILMAZ; aynı `id` geri döner. */
export function retryWebhookDelivery(
  oid: string,
  id: number,
  deliveryId: number,
  signal?: AbortSignal,
): Promise<{ ok: true; delivery: WebhookDelivery }> {
  return request<{ ok: true; delivery: WebhookDelivery }>(
    `/orgs/${encodeURIComponent(oid)}/webhooks/${id}/deliveries/${deliveryId}/retry`,
    { method: "POST", signal },
  );
}

/* --- §44 plan ve kota -------------------------------------------------------- */

export interface Plan {
  id: string;
  name: string;
  monthlyEventLimit: number;
  priceKurus: number;
  currency: string;
  features: Record<string, unknown>;
}

/** §44.4 — panelin kota banner'ı için tek bakışta durum. */
export interface OrgUsage {
  orgId: string;
  /** 'YYYY-MM' */
  month: string;
  events: number;
  limit: number;
  pct: number;
  /** Aşımın İLK anı; `null` = limit içinde. */
  overQuotaAt: string | null;
  plan: {
    id: string;
    name: string;
    priceKurus: number;
    features: Record<string, unknown>;
  };
  /** usage-job hiç koşmadıysa `false` — "ölçülmedi" ile "sıfır" ayrımı. */
  measured: boolean;
}

export function getPlans(signal?: AbortSignal): Promise<{ plans: Plan[] }> {
  return request<{ plans: Plan[] }>("/plans", { signal });
}

export function getOrgUsage(
  oid: string,
  signal?: AbortSignal,
): Promise<{ usage: OrgUsage }> {
  return request<{ usage: OrgUsage }>(
    `/orgs/${encodeURIComponent(oid)}/usage`,
    { signal },
  );
}

/* --- §45 faturalama ---------------------------------------------------------- */

/** §45.2 `subscriptions.status` — panelin rozet dili bu üç değerden türer. */
export type SubscriptionStatus = "active" | "past_due" | "canceled";

/** §45.2 `invoices.status` — sağlayıcının durumu; biz fatura ÜRETMEYİZ, aynalarız. */
export type InvoiceStatus = "paid" | "open" | "void";

/**
 * §45.2 `subscriptions` satırının panele açılan yüzü — alan adları camelCase
 * (`SubscriptionRecord` ile birebir).
 *
 * `providerRef` panelde GÖSTERİLMEZ ama tipte durur: fatura satırlarıyla eşleştirmek
 * (aynı ref'ten türeyen `<ref>_inv`) ve destek kaydında sağlayıcı kimliğini aramak
 * için tek bağdır.
 */
export interface Subscription {
  id: number;
  orgId: string;
  planId: string;
  status: SubscriptionStatus;
  /** Dönem sonu; sağlayıcı bildirmediyse null — panel '—' yazar, tarih UYDURMAZ. */
  currentPeriodEnd: string | null;
  /** 'mock' | 'stripe' (§45.1). */
  provider: string;
  providerRef: string | null;
  createdAt: string;
  updatedAt: string;
}

/** §45.2 `invoices` satırı — tutar KURUŞ cinsinden TAM SAYIDIR (float para yasak). */
export interface Invoice {
  id: number;
  orgId: string;
  amountKurus: number;
  currency: string;
  status: InvoiceStatus;
  providerRef: string | null;
  issuedAt: string;
  createdAt: string;
}

/** §45.2 abonelik ucunun yanıtı: abonelik + fatura aynası + org'un YÜRÜRLÜKTEKİ planı. */
export interface SubscriptionResponse {
  /** Abonelik yoksa null — bu bir HATA DEĞİL, ücretsiz plandaki org'un normal hâlidir. */
  subscription: Subscription | null;
  invoices: Invoice[];
  /** `orgs.plan_id` üzerinden çözülen plan; çözülemezse null (panel 'free' varsayar). */
  plan: Plan | null;
}

/**
 * §45.2 `GET /api/orgs/:oid/subscription` — abonelik, fatura geçmişi ve org'un planı
 * (org:member).
 *
 * Abonelik yokluğu 404 DEĞİL `null`dır (§45.2 kararı): ücretsiz plandaki org'un
 * aboneliği olmaması normaldir ve 404 sayfayı hata durumuna sokardı.
 */
export function getSubscription(
  oid: string,
  signal?: AbortSignal,
): Promise<SubscriptionResponse> {
  return request<SubscriptionResponse>(
    `/orgs/${encodeURIComponent(oid)}/subscription`,
    {
      signal,
    },
  );
}

/**
 * §45.1 `POST /api/orgs/:oid/checkout` — ödeme sayfasının URL'i (org:admin).
 *
 * Yanıt bir SÖZ DEĞİLDİR: sunucu hiçbir abonelik satırı yazmaz, durum yalnız
 * webhook'tan gelir. Bu yüzden panel de dönüşte "planınız yükseldi" DEMEZ — kullanıcıyı
 * `url`'e yollar ve döndüğünde durumu sunucudan yeniden okur.
 *
 * Hatalar durum + koddan okunur: 400 `free_plan` (ücretsiz plana geçiş ödeme değil
 * İPTALDİR), 400 `invalid_plan`, 403 (org:admin değil), 503 `payment_unavailable`.
 */
export function createCheckout(
  oid: string,
  planId: string,
  signal?: AbortSignal,
): Promise<{ url: string; planId: string }> {
  return request<{ url: string; planId: string }>(
    `/orgs/${encodeURIComponent(oid)}/checkout`,
    {
      method: "POST",
      body: { planId },
      signal,
    },
  );
}

/**
 * §45.3 `POST /api/orgs/:oid/subscription/cancel` — iptal DAİMA dönem sonundadır
 * (org:admin).
 *
 * Durum yanıtta HÂLÂ `active` döner ve bu doğrudur: ödenmiş dönemin sonuna kadar
 * hizmet sürer, `canceled`'a geçişi webhook yazar (§45.1). `effectiveAt` bilinmiyorsa
 * null gelir — panel o zaman tarih uydurmaz. Abonelik yoksa 404 `no_subscription`.
 */
export function cancelSubscription(
  oid: string,
  signal?: AbortSignal,
): Promise<{
  ok: true;
  effectiveAt: string | null;
  status: SubscriptionStatus;
}> {
  return request<{
    ok: true;
    effectiveAt: string | null;
    status: SubscriptionStatus;
  }>(`/orgs/${encodeURIComponent(oid)}/subscription/cancel`, {
    method: "POST",
    signal,
  });
}

/** §45.5 — mock ödeme sayfasının üretebileceği sonuçlar (sunucudaki `MOCK_OUTCOMES`). */
export const MOCK_OUTCOMES = ["paid", "failed", "exhausted"] as const;
export type MockOutcome = (typeof MOCK_OUTCOMES)[number];

/**
 * §45.1/§45.5 — checkout URL'inin panele taşıdığı İMZALI bağlam (`?ref&org&plan&sig`).
 *
 * Dördü de OLDUĞU GİBİ geri gönderilir: `sig`, `ref|org|plan` üçlüsünün imzasıdır ve
 * herhangi birini panelde "düzeltmek" imzayı bozar (sunucu 400 `invalid_signature`).
 * Kurcalanmış bir org/plan ile bedava yükseltme bu yüzden mümkün değildir.
 */
export interface MockPaymentParams {
  ref: string;
  org: string;
  plan: string;
  sig: string;
  outcome: MockOutcome;
}

/**
 * §45.5 `POST /api/billing/mock/complete` — mock ödeme sayfasının düğmesi (PUBLIC uç).
 *
 * Sunucu bu çağrıdan GERÇEK sağlayıcının göndereceği webhook gövdesini üretip kendi
 * imza doğrulamasından geçirir; yani panel "abonelik aktif" demez, yalnız olayın
 * uygulanıp uygulanmadığını (`applied`) ve hangi tipe dönüştüğünü öğrenir.
 * Gerçek sağlayıcı yapılandırıldığında uç 404 döner (mock'a özel yol yoktur).
 */
export function completeMockPayment(
  params: MockPaymentParams,
  signal?: AbortSignal,
): Promise<{ ok: true; applied: boolean; type: string }> {
  return request<{ ok: true; applied: boolean; type: string }>(
    "/billing/mock/complete",
    {
      method: "POST",
      body: {
        ref: params.ref,
        org: params.org,
        plan: params.plan,
        sig: params.sig,
        outcome: params.outcome,
      },
      signal,
      // Uç PUBLIC'tir (§45.4 muafiyet): buradaki bir 401 oturum düşmesi DEĞİLDİR.
      skipUnauthorizedHook: true,
    },
  );
}

/** Servis sağlığı — üst çubuktaki gösterge. */
export function getHealth(signal?: AbortSignal): Promise<HealthResponse> {
  return request<HealthResponse>("/health", { signal });
}

/**
 * §72.5/7 — KÖK `/health` (§19.4.4): giriş sayfasının oturumsuz sorabildiği tek uç.
 *
 * `getHealth` ile karıştırılmamalı: o, `/api/health`i çağırır ve `GET /api/**` →
 * viewer kuralına düşer, yani giriş yapmamış kullanıcı için 401'dir. Giriş sayfası
 * tanım gereği oturumsuzdur; "Hesap oluştur" bağlantısını çizip çizmeyeceğini
 * ancak muafiyet listesindeki kök uçtan öğrenebilir.
 *
 * Hata YUTULUR: sunucu erişilemezse bağlantı çizilmez. Kayıt bağlantısının yokluğu
 * bir arıza değil, en kötü ihtimalle eksik bir kolaylıktır — giriş formunun kendisi
 * bu sorgunun sonucunu BEKLEMEZ.
 */
export function getPublicHealth(
  signal?: AbortSignal,
): Promise<{ ok: boolean; signup: boolean } | null> {
  const base = /^https?:\/\//i.test(API_PREFIX)
    ? API_PREFIX.replace(/\/api$/, "")
    : (viteEnv("VITE_API_BASE")?.replace(/\/+$/, "") ?? "");
  return fetch(`${base}/health`, { signal, credentials: "omit" })
    .then((res) =>
      res.ok ? (res.json() as Promise<{ ok: boolean; signup: boolean }>) : null,
    )
    .catch(() => null);
}

/** §19.6 iç panel — müşteri başına maliyet/marj (yalnız ADMIN_TOKEN). */
export function getCosts(
  pid: string,
  signal?: AbortSignal,
): Promise<CostsResponse> {
  return request<CostsResponse>(`/projects/${encodeURIComponent(pid)}/costs`, {
    signal,
  });
}

/* --- §27.4 auth -------------------------------------------------------------- */

/** §27.4 — oturum sahibinin kimliği; servis belirtecinde `users` kaydı yoktur (§27.6). */
export interface AuthUser {
  id: string;
  email: string;
  /** Davet kabul edilirken yazılır; boş bırakılmışsa null. */
  fullName: string | null;
}

/** §27.1 — üyelik: kullanıcı ↔ proje ↔ rol. */
export interface AuthMembership {
  pid: string;
  role: Role;
}

/** §27.4 `GET /api/auth/me` yanıtı — route guard'ın tek kaynağı. */
export interface MeResponse {
  /**
   * Servis belirteciyle gezilirken sunucunun kullanıcı DÖNMEME ihtimali vardır
   * (§27.6: `users` kaydı yok) — bu yüzden null kabul edilir, panel o durumda rolü
   * `effectiveRole` ile 'owner' sayar.
   */
  user: AuthUser | null;
  memberships: AuthMembership[];
  /**
   * §27.6 — sunucu bu isteği SERVİS BELİRTECİ olarak doğruladı mı.
   *
   * Panel servis olduğunu istemci tarafında da bilir (token localStorage'dadır) ama
   * sunucunun sözü daha güvenilirdir: `memberships` boş dönen bir servis oturumunda
   * rolün 'owner' olduğunu bu bayrak söyler.
   */
  service?: boolean;
}

/**
 * §27.4 `POST /api/auth/login` — 200 + Set-Cookie · 401 `invalid_credentials` ·
 * 429 `too_many_attempts` (§27.4 rate limit).
 *
 * `skipUnauthorizedHook`: 401 burada "parola yanlış"tır, oturum düşmesi değil.
 */
export function login(
  email: string,
  password: string,
): Promise<{ user: AuthUser }> {
  return request<{ user: AuthUser }>("/auth/login", {
    method: "POST",
    body: { email, password },
    skipUnauthorizedHook: true,
  });
}

/** §27.4 `POST /api/auth/logout` — 204; sunucu Redis oturumunu ve çerezi siler. */
export function logout(): Promise<void> {
  return request<void>("/auth/logout", { method: "POST" });
}

/**
 * §27.4 `GET /api/auth/me` — 200 `{user, memberships}` · 401.
 *
 * Guard 401'i KENDİ ele alır (yönlendirmeyi o yapar), bu yüzden global kanca
 * atlanır; iki yönlendirme birbirini ezmesin.
 */
export function getMe(signal?: AbortSignal): Promise<MeResponse> {
  return request<MeResponse>("/auth/me", {
    signal,
    skipUnauthorizedHook: true,
  });
}

/** §27.4 `POST /api/auth/password` — 204 · 401 (current yanlış) · 400 (politika). */
export function changePassword(current: string, next: string): Promise<void> {
  return request<void>("/auth/password", {
    method: "POST",
    body: { current, next },
    // 401 = "mevcut parola yanlış"; kullanıcı oturumdan ATILMAZ (§27.4: mevcut oturum sürer).
    skipUnauthorizedHook: true,
  });
}

/**
 * §27.4 `POST /api/auth/invites` — 201 `{id, expiresAt}` · 403 (owner değil).
 *
 * Gövdeye `pid` de eklenir: şartname tabloda `{email, role}` yazar ama davet PROJE
 * başınadır (§27.2 `invites.pid`); tek projeli kurulumda sunucu varsayılana düşse de
 * panelin aktif projesini AÇIKÇA bildirmesi çok projeli kurulumda doğru davranıştır.
 */
/* --- §74 üyelik ve davet ------------------------------------------------------- */

/** §74.1 — proje üyesinin rolünü değiştirir (son owner düşürülemez: 409 `last_owner`). */
export function setMemberRole(
  pid: string,
  userId: string,
  role: Role,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/projects/${encodeURIComponent(pid)}/members/${encodeURIComponent(userId)}/role`,
    { method: "POST", body: { role } },
  );
}

/** §74.1 — üyeyi projeden çıkarır; org üyeliğine ve hesabına DOKUNULMAZ. */
export function removeMember(pid: string, userId: string): Promise<void> {
  return request<void>(
    `/projects/${encodeURIComponent(pid)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

/** §74.3 — bekleyen daveti iptal eder. */
export function cancelInvite(id: number): Promise<void> {
  return request<void>(`/auth/invites/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
  });
}

/** §74.3 — daveti yeniden gönderir: YENİ token üretilir, eskisi geçersizlenir. */
export function resendInvite(id: number): Promise<{ ok: true; id: number }> {
  return request<{ ok: true; id: number }>(
    `/auth/invites/${encodeURIComponent(String(id))}/resend`,
    { method: "POST" },
  );
}

/** §74.2 — org'a DOĞRUDAN davet; kabulde YALNIZ org üyeliği yazılır. */
export function createOrgInvite(
  oid: string,
  body: { email: string; role: OrgRole },
): Promise<{
  invite: { id: number; email: string; role: OrgRole; expiresAt: string };
}> {
  return request<{
    invite: { id: number; email: string; role: OrgRole; expiresAt: string };
  }>(`/orgs/${encodeURIComponent(oid)}/invites`, { method: "POST", body });
}

export interface OrgInviteRow {
  id: number;
  email: string;
  role: OrgRole;
  expiresAt: string;
  createdAt: string;
}

export function getOrgInvites(
  oid: string,
  signal?: AbortSignal,
): Promise<{ invites: OrgInviteRow[] }> {
  return request<{ invites: OrgInviteRow[] }>(
    `/orgs/${encodeURIComponent(oid)}/invites`,
    {
      signal,
    },
  );
}

/* --- §85 veri hakları (KVKK/GDPR) --------------------------------------------- */

/** §85.4 — silme talebinin denetim kaydı; `rowsDeleted` talebin KANITIDIR. */
export interface ErasureRequest {
  id: number;
  pid: string | null;
  /** `aid` (ziyaretçi) ya da MASKELİ e-posta (hesap). Ham adres ASLA. */
  subject: string;
  kind: "visitor" | "account";
  status: "pending" | "done" | "failed";
  rowsDeleted: number | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

/**
 * §85.2(b) — bir ziyaretçinin (`aid`) ham verisini siler.
 *
 * Talep MÜŞTERİDEN gelir, ziyaretçiden değil (§85.1): `aid` anonimdir ve DXP onu
 * bir insana bağlayamaz — ziyaretçiyi tanımak müşterinin işidir.
 */
export function eraseVisitor(
  pid: string,
  aid: string,
): Promise<{ request: { id: number; status: string; rowsDeleted: number } }> {
  return request<{
    request: { id: number; status: string; rowsDeleted: number };
  }>(`/projects/${encodeURIComponent(pid)}/erasure`, {
    method: "POST",
    body: { aid },
  });
}

export function getErasureRequests(
  pid: string,
  signal?: AbortSignal,
): Promise<{ requests: ErasureRequest[] }> {
  return request<{ requests: ErasureRequest[] }>(
    `/projects/${encodeURIComponent(pid)}/erasure`,
    { signal },
  );
}

/** §85.3 — taşınabilirlik: tek veri sahibinin ham olayları (JSONL indirme adresi). */
export function erasureExportUrl(pid: string, aid: string): string {
  return `${API_PREFIX}/projects/${encodeURIComponent(pid)}/erasure/export?aid=${encodeURIComponent(aid)}`;
}

/**
 * §85.2(a) — kullanıcı KENDİ hesabını siler; son owner ise 409 `last_owner`.
 *
 * Parola YIKICI İŞLEMDE KİMLİK TAZELEMEK için istenir (`/auth/password` deseni):
 * çalınmış ya da açık bırakılmış bir oturum, sahibinin hesabını kalıcı olarak
 * silebilirdi. SSO kullanıcısının parolası olmayabilir — sunucu o durumda parola
 * SORMAZ (yoksa hakkı elinden alınmış olurdu).
 */
export function deleteAccount(password: string): Promise<void> {
  return request<void>("/auth/account/delete", {
    method: "POST",
    body: { password },
  });
}

/* --- §73 hesap ---------------------------------------------------------------- */

/** §73.2 — ad-soyad. E-posta DEĞİŞTİRİLEMEZ (§71.2 KARAR 5: Faz 7 dışı). */
export function updateProfile(fullName: string): Promise<{ user: AuthUser }> {
  return request<{ user: AuthUser }>("/auth/profile", {
    method: "POST",
    body: { fullName },
  });
}

/** §73.1 — bu cihaz DIŞINDAKİ oturumları kapatır; mevcut oturum sürer. */
export function revokeOtherSessions(): Promise<{ ok: true; closed: number }> {
  return request<{ ok: true; closed: number }>("/auth/sessions/revoke-others", {
    method: "POST",
  });
}

/**
 * §73.3 — sıfırlama TALEBİ. Adres kayıtlı olsun olmasın AYNI yanıt döner (hesap
 * keşfi koruması); panel de bu yüzden "gönderildi mi" diye ayrım YAPMAZ.
 */
export function forgotPassword(
  email: string,
): Promise<{ ok: true; message: string }> {
  return request<{ ok: true; message: string }>("/auth/forgot", {
    method: "POST",
    body: { email },
    // Uç PUBLIC'tir: buradaki bir 401 oturum düşmesi DEĞİLDİR (mock ödeme deseni).
    skipUnauthorizedHook: true,
  });
}

/** §73.3 — token ile yeni parola. Başarıda TÜM oturumlar düşer. */
export function resetPassword(token: string, password: string): Promise<void> {
  return request<void>("/auth/reset", {
    method: "POST",
    body: { token, password },
    skipUnauthorizedHook: true,
  });
}

export function createInvite(
  pid: string,
  input: { email: string; role: Role },
): Promise<{ id: number; expiresAt: string }> {
  return request<{ id: number; expiresAt: string }>("/auth/invites", {
    method: "POST",
    body: { pid, email: input.email, role: input.role },
  });
}

/**
 * §27.4 `POST /api/auth/accept` — 200 `{user}` + Set-Cookie · 400 · 410 (süresi
 * geçmiş/kabul edilmiş token). Otomatik login olduğu için ayrıca `login` çağrılmaz.
 */
export function acceptInvite(input: {
  token: string;
  password: string;
  fullName: string;
}): Promise<{ user: AuthUser }> {
  return request<{ user: AuthUser }>("/auth/accept", {
    method: "POST",
    body: {
      token: input.token,
      password: input.password,
      fullName: input.fullName,
    },
    // Davet sayfası oturumsuz açılır; 410/400 burada kart içinde anlatılır.
    skipUnauthorizedHook: true,
  });
}

/* --- §43.2 self-serve kayıt --------------------------------------------------- */

/**
 * §43.2 — register/verify/resend uçlarının ORTAK olumlu yanıtı.
 *
 * `message` opsiyoneldir: panel kendi Türkçe cümlesini kurar, sunucununkine bağımlı
 * değildir — uç metnini değiştirse de kayıt ekranı bozulmaz.
 */
export interface SignupAck {
  ok: true;
  message?: string;
}

/**
 * §43.2 `POST /api/auth/register` — 200 `{ok, message}`.
 *
 * Hata sözleşmesi DURUM KODU + `ApiError.code` ikilisinden okunur:
 *   404 `not_found`         — §43.1 bayrağı KAPALI (özelliğin varlığı bile bildirilmez)
 *   400 `invalid_email`     — adres biçimi
 *   400 `weak_password`     — §27.3 politikası
 *   429 `too_many_attempts` — §43.4 IP başına günlük tavan
 *
 * VAR OLAN e-posta 200 döner (§43.2 hesap keşfi koruması): sunucu hiçbir şey yazmaz
 * ama yanıt yeni kayıtla BİREBİR aynıdır. Bu yüzden panel "hesabınız oluşturuldu"
 * DEMEZ — söyleyebileceği tek doğru cümle "doğrulama e-postası gönderildi"dir.
 */
export function register(input: {
  email: string;
  password: string;
  fullName?: string;
}): Promise<SignupAck> {
  const fullName = input.fullName?.trim() ?? "";
  return request<SignupAck>("/auth/register", {
    method: "POST",
    body: {
      email: input.email,
      password: input.password,
      ...(fullName === "" ? {} : { fullName }),
    },
    // Oturum EDİNME ucu: buradaki hiçbir hata "oturumun düştü" değildir (§27.7 kancası).
    skipUnauthorizedHook: true,
  });
}

/**
 * §43.2 `POST /api/auth/verify` — 200 `{ok, user}` · 400 `invalid_token` · 404 (bayrak
 * kapalı). Token TEK KULLANIMLIKTIR; kullanılmış/süresi geçmiş/hiç var olmamış token
 * sunucuda AYNI 400'ü alır (token uzayını yoklayana bilgi verilmez).
 *
 * Doğrulama oturum AÇMAZ (accept ucundan farkı budur): kullanıcı `/giris`'e gider.
 */
export function verifyEmail(
  token: string,
): Promise<{ ok: true; user: AuthUser }> {
  return request<{ ok: true; user: AuthUser }>("/auth/verify", {
    method: "POST",
    body: { token },
    skipUnauthorizedHook: true,
  });
}

/**
 * §43.2 `POST /api/auth/verify/resend` — 200 · 429 `too_many_attempts` · 404 (kapalı).
 *
 * Bilinmeyen ya da ZATEN doğrulanmış adres de 200 alır (hesap keşfi koruması), bu
 * yüzden panel "e-posta yolda" der; "hesabınız bulundu" DEMEZ.
 */
export function resendVerification(email: string): Promise<SignupAck> {
  return request<SignupAck>("/auth/verify/resend", {
    method: "POST",
    body: { email },
    skipUnauthorizedHook: true,
  });
}

/**
 * §43.2/4 — sihirbazın son adımındaki `<script src="…/sdk.js">` adresi.
 *
 * NEDEN api.ts: bu bir YAPILANDIRMA sorusudur (SDK hangi origin'den servis ediliyor),
 * sayfa sorusu değil. `/sdk.js` §10 gereği ingest-api'nin KÖKÜNDEDİR, `/api` önekinin
 * altında değil — bu yüzden prefix'ten türetilirken sondaki `/api` atılır.
 *
 * Sıra: açık `VITE_SDK_URL` → mutlak `VITE_API_PREFIX` → geliştirme varsayılanı.
 * Göreli prefix'ten (`/api`) mutlak adres türetilemez: panelin kendi origin'i (5173)
 * SDK'yı servis etmez, üstelik müşteri snippet'i KENDİ sitesine yapıştıracaktır —
 * oraya panelin adresini yazmak sessizce çalışmayan bir kurulum üretirdi.
 */
export function sdkScriptUrl(): string {
  const explicit = viteEnv("VITE_SDK_URL");
  if (explicit !== undefined) return explicit.replace(/\/+$/, "");
  if (/^https?:\/\//i.test(API_PREFIX)) {
    return `${API_PREFIX.replace(/\/api$/, "")}/sdk.js`;
  }
  const base = viteEnv("VITE_API_BASE");
  if (base !== undefined) return `${base.replace(/\/+$/, "")}/sdk.js`;
  // §13 boş durum kartındaki (EmptyState) adresle AYNI varsayılan — iki yerde iki
  // farklı örnek adres, kullanıcıya hangisinin doğru olduğunu sordururdu.
  return "http://localhost:4000/sdk.js";
}

/**
 * §27.7 "Ayarlar → Üyeler" — proje üyeleri (ad, e-posta, rol, son giriş).
 *
 * Uç §27.4 tablosunda YOKTUR (o tablo oturum/mutasyon uçlarını sayar); okuma ucu
 * §27.5'teki `GET /api/*` → viewer satırına düşer. Yanıt satırları iki yazımda da
 * gelebildiği için `normalizeMember`'dan geçirilir.
 */
export function listMembers(
  pid: string,
  signal?: AbortSignal,
): Promise<MemberRow[]> {
  return request<unknown[]>(`/projects/${encodeURIComponent(pid)}/members`, {
    signal,
  }).then((rows) =>
    (Array.isArray(rows) ? rows : []).map(normalizeMember).filter(isNotNull),
  );
}

/** §27.7 — bekleyen davetler (kabul edilmemiş satırlar); `token` DÖNMEZ (§27.2). */
export function listInvites(
  pid: string,
  signal?: AbortSignal,
): Promise<InviteRow[]> {
  return request<unknown[]>(`/projects/${encodeURIComponent(pid)}/invites`, {
    signal,
  }).then((rows) =>
    (Array.isArray(rows) ? rows : []).map(normalizeInvite).filter(isNotNull),
  );
}

/** `.filter(Boolean)` tipi daraltmadığı için küçük yardımcı. */
function isNotNull<T>(value: T | null): value is T {
  return value !== null;
}

/* -------------------------------------------------------------------------- */
/* Yardımcılar                                                                  */
/* -------------------------------------------------------------------------- */

/** §13.1 — "30 günlük sparkline": overview'un varsayılan tarih aralığı. */
export function lastNDaysRange(
  days = 30,
  now: Date = new Date(),
): { from: string; to: string } {
  const to = new Date(now);
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

/**
 * react-query anahtar fabrikası — sayfalar aynı anahtarları kullansın diye tek yerde.
 * Mutasyon sonrası `queryClient.invalidateQueries({ queryKey: qk.insights(pid) })` gibi.
 */
/** §25.7 — Atlas özeti kaydı. */
export interface NarrativeResponse {
  content: string;
  model: string;
  costUsd: number;
  createdAt: string;
  kind: string;
  /** §25.5 — tavan durumu; rozet TIKLAMADAN görünebilsin diye yanıtta taşınır. */
  caps?: { monthlyFull: boolean; dailyFull: boolean; monthlyUsd: number };
}

/** §25.7 — en son anlatı; kayıt yoksa uç 404 döner (kart gizlenir). */
export function getNarrative(
  pid: string,
  signal?: AbortSignal,
): Promise<NarrativeResponse> {
  return request<NarrativeResponse>(
    `/projects/${encodeURIComponent(pid)}/narrative`,
    { signal },
  );
}

/** §25.7 — yeniden üretim talebi. Tavan dolduysa uç 429 döner. */
export function refreshNarrative(pid: string): Promise<{ queued: boolean }> {
  return request<{ queued: boolean }>(
    `/projects/${encodeURIComponent(pid)}/narrative/refresh`,
    { method: "POST" },
  );
}

/* -------------------------------------------------------------------------- */
/* §19.3 uzun vadeli yönetici raporu                                           */
/* -------------------------------------------------------------------------- */

/** §19.3 — panelin okuduğu tek uzun-vade kaynağı. Varsayılan dönem. */
export const EXEC_REPORT_PERIOD = "last_24m";

/**
 * §19.3 — uç ya hazır raporu ya da `{status:'missing'}` döner.
 *
 * Ayrım TİPTE tutulur çünkü panelin üç hâli vardır ve ikisi birbirine benzer:
 * "hiç hazırlanmamış" (missing), "kuyruğa alınmış ama job koşmamış" (payload var,
 * `months` BOŞ) ve "hazır". İkincisini `payload === undefined` ile ayırt etmek
 * mümkün olmazdı — refresh ucu 202'den önce BOŞ payload yazar.
 */
export interface ExecReportRecord {
  payload: ExecReportPayload;
  /** ISO-8601 UTC — "şu tarihte hazırlandı" bilgisinin kaynağı (§19.3). */
  computed_at: string;
}

export type ExecReportResponse = { status: "missing" } | ExecReportRecord;

/** §19.3 — hazır rapor; CANLI ağır sorgu ÇALIŞMAZ, son hesaplanmış JSON okunur. */
export function getExecReport(
  pid: string,
  period: string = EXEC_REPORT_PERIOD,
  signal?: AbortSignal,
): Promise<ExecReportResponse> {
  return request<ExecReportResponse>(
    `/projects/${encodeURIComponent(pid)}/exec-report?period=${encodeURIComponent(period)}`,
    { signal },
  );
}

/** §19.3 — "yenile" job'ı KUYRUĞA atar (202); hesaplama gece penceresindedir. */
export function refreshExecReport(
  pid: string,
  period: string = EXEC_REPORT_PERIOD,
): Promise<{ status: string }> {
  return request<{ status: string }>(
    `/projects/${encodeURIComponent(pid)}/exec-report/refresh?period=${encodeURIComponent(period)}`,
    { method: "POST" },
  );
}

export const qk = {
  /** §42.5 — kullanıcının org'ları; oturum değişince `queryClient.clear()` temizler. */
  orgs: () => ["orgs"] as const,
  orgProjects: (oid: string) => ["org-projects", oid] as const,
  orgMembers: (oid: string) => ["org-members", oid] as const,
  orgUsage: (oid: string) => ["org-usage", oid] as const,
  /** §75.4 — zil listesi + okunmamış sayısı; "okundu" sonrası geçersizlenir. */
  orgNotices: (oid: string) => ["org-notices", oid] as const,
  /** §45.2 — org başına abonelik + fatura geçmişi; checkout/iptal sonrası geçersizlenir. */
  orgSubscription: (oid: string) => ["org-subscription", oid] as const,
  plans: () => ["plans"] as const,
  ingestStatus: (pid: string) => ["ingest-status", pid] as const,
  overview: (pid: string, from: string, to: string, compare = false) =>
    ["overview", pid, from, to, compare ? "cmp" : ""] as const,
  goals: (pid: string) => ["goals", pid] as const,
  experiments: (pid: string) => ["experiments", pid] as const,
  experimentResults: (id: string) => ["experimentResults", id] as const,
  funnel: (
    pid: string,
    segment?: FunnelSegmentFilter,
    segmentId?: string,
    compare = false,
  ) =>
    [
      "funnel",
      pid,
      segment?.dimension ?? "",
      segment?.value ?? "",
      segmentId ?? "",
      compare ? "cmp" : "",
    ] as const,
  insights: (pid: string, status?: InsightStatus) =>
    ["insights", pid, status ?? "all"] as const,
  /**
   * §13.3 — `step` anahtarın PARÇASIDIR: dışarıda kalsaydı funnel'da adım
   * değiştirmek önbellekteki eski adımın listesini geri verirdi (denetim bulgusu
   * A1'in önbellek hâli).
   */
  sessions: (pid: string, filter?: SessionFilter, segmentId?: string, step?: number) =>
    ["sessions", pid, filter ?? "all", segmentId ?? "", step ?? -1] as const,
  /** §92.3 — proje başına segment listesi (seçiciler paylaşır). */
  segments: (pid: string) => ["segments", pid] as const,
  /** §93.3 — kohort matrisi. */
  retention: (pid: string) => ["retention", pid] as const,
  sessionEvents: (pid: string, sid: string) =>
    ["session-events", pid, sid] as const,
  narrative: (pid: string) => ["narrative", pid] as const,
  sessionReplay: (pid: string, sid: string) =>
    ["session-replay", pid, sid] as const,
  /** Chunk'lar ayrı ayrı önbelleklenir — oynatıcı ileri/geri sarınca yeniden indirilmesin. */
  replayChunk: (pid: string, sid: string, seq: number) =>
    ["replay-chunk", pid, sid, seq] as const,
  /**
   * §23.4 — anahtar TÜM sorgu parametrelerini taşır: desen/cihaz/mod/tarih
   * değişimi ayrı bir haritadır, önbellek çakışırsa panel yanlış ızgarayı çizer.
   */
  heatmap: (pid: string, params: HeatmapQuery) =>
    [
      "heatmap",
      pid,
      params.path,
      params.device,
      params.mode ?? "click",
      params.from ?? "",
      params.to ?? "",
    ] as const,
  heatmapPaths: (pid: string) => ["heatmap-paths", pid] as const,
  /** §24.7 — proje başına tek özet (son tamamlanmış tarama). */
  seo: (pid: string) => ["seo", pid] as const,
  /**
   * §24.7 — çekmece içeriği check'e göre ayrı önbelleklenir; anahtar limiti de taşır
   * ki "50 satır" ile "200 satır" isteği aynı girdiyi paylaşıp yarım liste göstermesin.
   */
  seoPages: (pid: string, check?: string, limit?: number) =>
    ["seo-pages", pid, check ?? "", limit ?? 0] as const,
  /** §26.6 — proje başına abone listesi; ekleme/silme sonrası bu anahtar geçersizlenir. */
  subscriptions: (pid: string) => ["subscriptions", pid] as const,
  /**
   * §27.7 — oturum kimliği. Anahtar proje TAŞIMAZ: `me` kullanıcıya bağlıdır,
   * proje değiştirmek onu geçersizlemez. Login/logout sonrası bu anahtar temizlenir.
   */
  me: () => ["auth-me"] as const,
  /** §27.7 — proje üyeleri (Ayarlar → Üyeler). */
  members: (pid: string) => ["members", pid] as const,
  /** §27.7 — bekleyen davetler; davet yaratınca bu anahtar geçersizlenir. */
  invites: (pid: string) => ["invites", pid] as const,
  /** §74.4 — org'un bekleyen davetleri (proje davetlerinden AYRI anahtar ailesi). */
  orgInvites: (oid: string) => ["org-invites", oid] as const,
  /** §85.5 — silme talepleri (kanıt listesi). */
  erasure: (pid: string) => ["erasure", pid] as const,
  projects: () => ["projects"] as const,
  health: () => ["health"] as const,
  costs: (pid: string) => ["costs", pid] as const,
  /** §19.3 — dönem anahtarın PARÇASIDIR: farklı dönemler ayrı cache satırlarıdır. */
  execReport: (pid: string, period: string) =>
    ["exec-report", pid, period] as const,
  /** §47 — uçlar org'a, teslimatlar UCA asılıdır: iki ayrı anahtar ailesi. */
  webhooks: (oid: string) => ["webhooks", oid] as const,
  webhookDeliveries: (oid: string, id: number) =>
    ["webhook-deliveries", oid, id] as const,
  /** §46.5 — org SSO yapılandırması. */
  orgSso: (oid: string) => ["org-sso", oid] as const,
};

/** §27 auth tipleri de tek kapıdan çıksın — sayfalar `lib/auth.js`'i ayrıca almasın. */
export type { InviteRow, MemberRow, Role };

/** Şema tiplerini tek noktadan yeniden dışa aç — sayfalar `@dxp/schema`'yı ayrıca almasın. */
export type {
  Channel,
  CostProfile,
  DerivedType,
  Device,
  EmailCadence,
  EventType,
  Funnel,
  FunnelSegmentBreakdown,
  FunnelStepMetrics,
  Goal,
  IngestStatusResponse,
  Insight,
  InsightStatus,
  OverviewResponse,
  Project,
  ProjectCost,
  ReplayChunkMeta,
  ReplayListResponse,
  SegmentConditions,
};
